// The Documents page (docs/cockpit/spec.md): every Document LAMP issued, newest first, with its customer and
// amount, filtered by date and searched by the server. Issued Documents never change; a row opens its PDF. Below them,
// a month's Accounting Export to Tally downloads for the CA (#503); the Application API gives it only to those who may
// see the accounts. For a business whose books are kept in Tally, the Books Link (V-13) takes its place: the files of
// vouchers not yet sent are made and kept here, and the postings kept out are listed with what is missing. Below them,
// Books Reconciliation (V-18): each period LAMP and a Tally day book were compared for, bill by bill.
import { useCallback, useEffect, useState } from "react";
import { rupees, tallyExportHref, type Api } from "../api.js";
import type { BooksFileMade, BooksLinkApi, BooksLinkView } from "./books-link-api.js";
import { BooksReconciliation } from "./BooksReconciliation.js";
import { useLanguage, useWords } from "../i18n/words.js";
import { ListSearch, ListView, dayOf, useList } from "../lists/List.js";

/** Last month, `YYYY-MM`: what a CA usually asks for. */
function lastMonth(today: Date): string {
  const month = new Date(Date.UTC(today.getFullYear(), today.getMonth() - 1, 1));
  return month.toISOString().slice(0, 7);
}

function TallyExport({ today }: { readonly today: Date }) {
  const w = useWords();
  const [month, setMonth] = useState(lastMonth(today));
  const valid = /^\d{4}-(0[1-9]|1[0-2])$/.test(month);
  return (
    <section className="tally-export" aria-label={w.documentsTallyTitle}>
      <h3>{w.documentsTallyTitle}</h3>
      <p className="muted">{w.documentsTallyIntro}</p>
      <div className="toolbar">
        <label className="field-inline">
          {w.documentsTallyMonth} <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        {valid ? (
          <a className="btn" href={tallyExportHref(month)} download={`tally-vouchers-${month}.xml`}>
            {w.documentsTallyDownload(month)}
          </a>
        ) : null}
      </div>
    </section>
  );
}

/** The Books Link: where the books are kept, the vouchers not yet sent and the files made. */
function BooksLink({
  api,
  view,
  onView,
}: {
  readonly api: BooksLinkApi;
  readonly view: BooksLinkView;
  readonly onView: (view: BooksLinkView) => void;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [made, setMade] = useState<BooksFileMade>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);
  const act = async (doing: () => Promise<void>) => {
    setBusy(true);
    setProblem(undefined);
    try {
      await doing();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };
  const keep = (books: "lamp" | "tally") => act(async () => onView(await api.keepBooksIn(books)));
  const make = (again?: string) =>
    act(async () => {
      setMade(await api.make(again));
      onView(await api.shown());
    });
  const numberOf = (id: string) => view.files.find((f) => f.id === id)?.number ?? 0;

  if (view.books === "lamp")
    return view.may.keep ? (
      <section className="books-link" aria-label={w.booksLinkTitle}>
        <p className="muted">{w.booksLinkInLamp}</p>
        <button type="button" className="btn" disabled={busy} onClick={() => void keep("tally")}>
          {w.booksLinkKeepInTally}
        </button>
        {problem ? <p role="alert">{problem}</p> : null}
      </section>
    ) : null;
  return (
    <section className="books-link" aria-label={w.booksLinkTitle}>
      <h3>{w.booksLinkTitle}</h3>
      <p className="muted">{w.booksLinkIntro(dayOf(view.since ?? "", language))}</p>
      <div className="toolbar">
        <span>{w.booksLinkUnsent(view.unsent)}</span>
        {view.may.make ? (
          <button type="button" className="btn" disabled={busy} onClick={() => void make()}>
            {w.booksLinkMake}
          </button>
        ) : null}
        {view.may.keep ? (
          <button type="button" className="btn ghost" disabled={busy} onClick={() => void keep("lamp")}>
            {w.booksLinkKeepInLamp}
          </button>
        ) : null}
      </div>
      {problem ? <p role="alert">{problem}</p> : null}
      {made ? (
        <div role="status">
          {made.files.length > 0 ? (
            made.files.map((f) => <p key={f.id}>{w.booksLinkMade(f.name, f.vouchers)}</p>)
          ) : (
            <p>{w.booksLinkNothing}</p>
          )}
          {made.held.length > 0 ? (
            <>
              <p>{w.booksLinkHeld}</p>
              <ul>
                {made.held.map((h) => (
                  <li key={h.posting}>
                    {h.document}: {h.problems.join(" ")}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : null}
      {view.files.length > 0 ? (
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.booksLinkFile}</th>
              <th scope="col" className="r">
                {w.booksLinkVouchers}
              </th>
              <th scope="col">{w.booksLinkMadeAt}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {view.files.map((f) => (
              <tr key={f.id}>
                <th scope="row">
                  <a className="linkish" href={f.href} download={f.name}>
                    {f.name}
                  </a>
                  {f.again ? <span className="muted"> ({w.booksLinkAgainOf(numberOf(f.again))})</span> : null}
                </th>
                <td className="r">{f.vouchers}</td>
                <td>{dayOf(f.made_at, language)}</td>
                <td>
                  {view.may.make ? (
                    <button type="button" className="btn ghost" disabled={busy} onClick={() => void make(f.id)}>
                      {w.booksLinkAgain}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

export function Documents({
  api,
  initialQuery = "",
  today = new Date(),
}: {
  api: Pick<Api, "documents"> & Partial<Pick<Api, "booksLink" | "booksReconciliation">>;
  initialQuery?: string;
  /** Today, for the month the Tally export offers first. */
  today?: Date;
}) {
  const w = useWords();
  const { language } = useLanguage();
  const [q, setQ] = useState(initialQuery);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { loaded, setPage } = useList(api.documents, { q, from, to });
  // Where the books are kept: unknown until read, and not shown to someone who may not see the accounts.
  const [books, setBooks] = useState<BooksLinkView>();
  const booksLink = api.booksLink;
  const readBooks = useCallback(() => {
    if (!booksLink) return;
    booksLink.shown().then(setBooks, () => setBooks(undefined));
  }, [booksLink]);
  useEffect(readBooks, [readBooks]);
  return (
    <>
      <div className="hello">
        <h2>{w.documentsTitle}</h2>
        <p className="muted">{w.documentsIntro}</p>
      </div>
      <div className="toolbar">
        <label className="field-inline">
          {w.documentsFrom}{" "}
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field-inline">
          {w.documentsTo}{" "}
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
        <ListSearch label={w.documentsSearch} initial={initialQuery} onSearch={setQ} />
      </div>
      <ListView
        loaded={loaded}
        what={w.documentsWhat}
        empty={q || from || to ? w.documentsNoMatch : w.documentsNone}
        onPage={setPage}
      >
        {(rows) => (
          <table className="tbl">
            <thead>
              <tr>
                <th scope="col">{w.documentsDocument}</th>
                <th scope="col">{w.documentsCustomer}</th>
                <th scope="col">{w.documentsDate}</th>
                <th scope="col" className="r">
                  {w.documentsAmount}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = row.number ?? w.documentsTaxInvoice;
                return (
                  <tr key={row.id}>
                    <th scope="row">
                      {row.pdf ? (
                        <a className="linkish" href={row.pdf} target="_blank" rel="noopener">
                          {name}
                        </a>
                      ) : (
                        name
                      )}
                    </th>
                    <td>{row.counterparty ?? "—"}</td>
                    <td>{dayOf(row.date, language)}</td>
                    <td className="r">{row.amount === undefined ? "—" : rupees(row.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ListView>
      {booksLink && books ? <BooksLink api={booksLink} view={books} onView={setBooks} /> : null}
      {books?.books === "tally" ? null : <TallyExport today={today} />}
      {api.booksReconciliation ? <BooksReconciliation api={api.booksReconciliation} /> : null}
    </>
  );
}
